import { Button, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Slider, Textarea, Tabs, TabsContent, TabsList, TabsTrigger, Checkbox, Alert, AlertDescription } from '@creanizer/ui'
import type { SavedPlot } from '@creanizer/types'
import type { CharacterInput, CharacterResult, DetailedLockedSections } from '@creanizer/types'
import { Upload, RefreshCw, Dices, Globe, Users, MapPin, Shield, Info, ChevronDown, ChevronUp, Search, Lock, Unlock, FileText, X, Image, Package } from 'lucide-react'
import { type ChangeEvent, useState, useEffect, useRef } from 'react'
import { getRandomKeywordsFromAll } from '../../../../data/characterKeywords'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useCommunityStore } from '../../../../stores/communityStore'
import { useSocietyStore } from '../../../../stores/societyStore'
import { useItemStore } from '../../../../stores/itemStore'
import { useSkillStore } from '../../../../stores/skillStore'
import { generateWorldContextText, generateCharacterContext } from '@creanizer/utils'
import { readMultipleFiles, FILE_ACCEPT, formatFileSize } from '../../utils/fileReader'

interface CharacterFormProps {
  input: CharacterInput
  onInputChange: (input: CharacterInput) => void
  onGenerate: (preparedInput?: CharacterInput) => void
  isGenerating: boolean
  savedPlots?: SavedPlot[]
  useStreaming?: boolean
  onStreamingChange?: (value: boolean) => void
  partialRegenerateMode?: boolean
  existingResult?: CharacterResult | null
  lockedSections?: DetailedLockedSections
  onLockedSectionsChange?: (sections: DetailedLockedSections) => void
  onPartialRegenerateModeChange?: (mode: boolean) => void
  updateMode?: boolean
  updateSource?: string
  onUpdateSourceChange?: (source: string) => void
  onUpdateModeChange?: (mode: boolean) => void
}

// 役割の選択肢
const ROLE_OPTIONS = [
  { value: 'protagonist', label: '主人公' },
  { value: 'heroine', label: 'ヒロイン' },
  { value: 'rival', label: 'ライバル' },
  { value: 'mentor', label: '師匠・メンター' },
  { value: 'sidekick', label: '相棒・仲間' },
  { value: 'antagonist', label: '敵役・悪役' },
  { value: 'support', label: 'サポート役' },
  { value: 'comic_relief', label: 'コメディリリーフ' },
  { value: 'mysterious', label: '謎の人物' },
  { value: 'other', label: 'その他' }
]

// ジャンルの選択肢
const GENRE_OPTIONS = [
  { value: 'fantasy', label: 'ファンタジー' },
  { value: 'sf', label: 'SF' },
  { value: 'mystery', label: 'ミステリー' },
  { value: 'romance', label: '恋愛' },
  { value: 'slice_of_life', label: '日常' },
  { value: 'horror', label: 'ホラー' },
  { value: 'action', label: 'アクション' },
  { value: 'comedy', label: 'コメディ' },
  { value: 'other', label: 'その他' }
]

interface Keyword {
  value: string
  isCustom: boolean
  index?: number
}

export function CharacterForm({
  input,
  onInputChange,
  onGenerate,
  isGenerating,
  savedPlots = [],
  useStreaming,
  onStreamingChange,
  partialRegenerateMode = false,
  existingResult = null,
  lockedSections = {},
  onLockedSectionsChange,
  onPartialRegenerateModeChange,
  updateMode = false,
  updateSource = '',
  onUpdateSourceChange,
  onUpdateModeChange
}: CharacterFormProps) {
  const { savedWorlds } = useWorldBuilderStore()
  const { characters } = useCharacterStore()
  const { savedCommunities } = useCommunityStore()
  const { savedSocieties } = useSocietyStore()
  const { items: savedItems } = useItemStore()
  const { skills: savedSkills } = useSkillStore()
  const [selectedPlotId, setSelectedPlotId] = useState<string>('')
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ id: string; name: string; content: string; size: number }>>([])  // 複数ファイル対応
  const [uploadedImages, setUploadedImages] = useState<Array<{ id: string; name: string; data: string; size: number }>>([])  // 画像ファイル
  const fileInputRef = useRef<HTMLInputElement>(null)  // ファイル入力への参照
  const imageInputRef = useRef<HTMLInputElement>(null)  // 画像入力への参照
  const [selectedWorldId, setSelectedWorldId] = useState<string>('')
  const [selectedCharacterId, setSelectedCharacterId] = useState<string>('')  // 単一選択（後方互換性）
  const [relationshipType, setRelationshipType] = useState<string>('')  // 単一関係性（後方互換性）
  const [selectedCharacterIds, setSelectedCharacterIds] = useState<string[]>([])  // 複数選択
  const [characterRelationships, setCharacterRelationships] = useState<Record<string, string>>({})  // 各キャラクターとの関係性
  const [showCharacterSelector, setShowCharacterSelector] = useState(false)  // キャラクター選択エリアの表示/非表示
  const [characterSearchQuery, setCharacterSearchQuery] = useState('')  // キャラクター検索
  const [selectedCommunityId, setSelectedCommunityId] = useState<string>('')
  const [communityRelationship, setCommunityRelationship] = useState<string>('')
  const [selectedSocietyId, setSelectedSocietyId] = useState<string>('')
  const [societyRelationship, setSocietyRelationship] = useState<string>('')
  // アイテム連携
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([])
  const [itemRelationships, setItemRelationships] = useState<Record<string, string>>({})
  const [showItemSelector, setShowItemSelector] = useState(false)
  const [itemSearchQuery, setItemSearchQuery] = useState('')
  // スキル連携
  const [selectedSkillIds, setSelectedSkillIds] = useState<string[]>([])
  const [skillRelationships, setSkillRelationships] = useState<Record<string, string>>({})
  const [showSkillSelector, setShowSkillSelector] = useState(false)
  const [skillSearchQuery, setSkillSearchQuery] = useState('')

  // 基本情報のモード管理
  const [nameMode, setNameMode] = useState<'auto' | 'custom'>('custom')
  const [ageMode, setAgeMode] = useState<'auto' | 'custom'>('custom')
  const [roleMode, setRoleMode] = useState<'select' | 'custom'>('select')
  const [selectedRole, setSelectedRole] = useState<string>('protagonist')
  const [customRole, setCustomRole] = useState<string>('')
  
  // キーワード管理（3つのキーワード）
  const [keywords, setKeywords] = useState<Keyword[]>([])
  const [keywordModes, setKeywordModes] = useState<('random' | 'custom')[]>(['random', 'random', 'random'])
  
  // 追加設定
  const [additionalSettings, setAdditionalSettings] = useState<string>(input.additionalSettings || '')

  // 初期キーワードの設定
  useEffect(() => {
    if (input.mode === 'standard') {
      // inputにキーワードがある場合はそれを使用
      if (input.keywords && input.keywords.length > 0) {
        setKeywords(input.keywords.map((value, index) => ({
          value,
          isCustom: false,
          index
        })))
      } else if (keywords.length === 0) {
        // キーワードがない場合は初期化
        const initialKeywords = getRandomKeywordsFromAll(3)
        setKeywords(initialKeywords.map((value, index) => ({
          value,
          isCustom: false,
          index
        })))
      }
    }
  }, [input.mode, input.keywords])

  // 初回マウント時のみ初期値を設定
  useEffect(() => {
    if (input.name) setNameMode('custom')
    if (input.age) setAgeMode('custom')
    if (input.role) setSelectedRole(input.role)
    if (input.additionalSettings) setAdditionalSettings(input.additionalSettings)
  }, [])

  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    try {
      const { combinedText, fileInfos, errors } = await readMultipleFiles(files)
      
      // エラーがある場合は表示
      if (errors.length > 0) {
        alert(`以下のファイルの読み込みに失敗しました:\n${errors.join('\n')}`)
      }
      
      // 成功したファイルを追加
      const newFiles = fileInfos
        .filter(info => !info.error && info.text)
        .map(info => ({
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          name: info.fileName,
          content: info.text,
          size: info.text.length
        }))
      
      setUploadedFiles(prev => [...prev, ...newFiles])
      
      // uploadedContentを更新（すべてのファイル内容を結合）
      const allFilesContent = [...uploadedFiles, ...newFiles]
        .map(file => `【ファイル: ${file.name}】\n${file.content}`)
        .join('\n\n')
      onInputChange({ ...input, uploadedContent: allFilesContent })
    } catch (error) {
      console.error('ファイル読み込みエラー:', error)
      alert('ファイルの読み込みに失敗しました')
    }
    
    // ファイル選択をリセット
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }
  
  const handleFileRemove = (fileId: string) => {
    const newFiles = uploadedFiles.filter(f => f.id !== fileId)
    setUploadedFiles(newFiles)

    // uploadedContentを更新
    const allFilesContent = newFiles
      .map(file => `【ファイル: ${file.name}】\n${file.content}`)
      .join('\n\n')
    onInputChange({ ...input, uploadedContent: allFilesContent || undefined })
  }

  const handleImageUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const maxSize = 20 * 1024 * 1024 // 20MB制限
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']

    const newImages: Array<{ id: string; name: string; data: string; size: number }> = []

    for (const file of Array.from(files)) {
      if (!validTypes.includes(file.type)) {
        alert(`${file.name} はサポートされていない形式です（対応: JPEG/PNG/GIF/WebP）`)
        continue
      }

      if (file.size > maxSize) {
        alert(`${file.name} はファイルサイズが大きすぎます (最大20MB)`)
        continue
      }

      try {
        const reader = new FileReader()
        const base64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => resolve(reader.result as string)
          reader.onerror = reject
          reader.readAsDataURL(file)
        })

        newImages.push({
          id: `img_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          name: file.name,
          data: base64,
          size: file.size
        })
      } catch (error) {
        console.error('画像読み込みエラー:', error)
        alert(`${file.name} の読み込みに失敗しました`)
      }
    }

    if (newImages.length > 0) {
      const allImages = [...uploadedImages, ...newImages].slice(0, 5) // 最大5枚まで
      setUploadedImages(allImages)
      // input.imagesを更新
      onInputChange({ ...input, images: allImages.map(img => img.data) })
    }

    // input要素をリセット
    if (imageInputRef.current) {
      imageInputRef.current.value = ''
    }
  }

  const handleImageRemove = (imageId: string) => {
    const newImages = uploadedImages.filter(img => img.id !== imageId)
    setUploadedImages(newImages)
    // input.imagesを更新
    onInputChange({ ...input, images: newImages.length > 0 ? newImages.map(img => img.data) : undefined })
  }

  const handleModeChange = (mode: 'standard' | 'idea') => {
    onInputChange({ ...input, mode })
  }

  const handlePlotSelect = (plotId: string) => {
    setSelectedPlotId(plotId)
    const plot = savedPlots.find(p => p.id === plotId)
    if (plot) {
      const plotContext = `
タイトル: ${plot.result.title}
テーマ: ${plot.result.themes.join('、')}

あらすじ:
${plot.result.synopsis}

主要キャラクター:
${plot.result.characters.map(c => `- ${c.name}: ${c.role} - ${c.description || ''}`).join('\n')}
      `.trim()
      
      onInputChange({ ...input, plotContext })
    }
  }

  // キーワードの再抽選
  const rerollKeyword = (index: number) => {
    const newKeyword = getRandomKeywordsFromAll(1, Date.now() + index)[0]
    const newKeywords = [...keywords]
    newKeywords[index] = {
      value: newKeyword,
      isCustom: false,
      index: Math.floor(Math.random() * 700) // キーワード総数
    }
    setKeywords(newKeywords)
  }

  // すべてのキーワードを再抽選
  const rerollAllKeywords = () => {
    const newKeywords = getRandomKeywordsFromAll(3)
    setKeywords(newKeywords.map((value, index) => ({
      value,
      isCustom: false,
      index
    })))
    setKeywordModes(['random', 'random', 'random'])
  }

  // キーワードモードの切り替え
  const toggleKeywordMode = (index: number) => {
    const newModes = [...keywordModes]
    newModes[index] = newModes[index] === 'random' ? 'custom' : 'random'
    setKeywordModes(newModes)
    
    if (newModes[index] === 'random') {
      rerollKeyword(index)
    } else {
      const newKeywords = [...keywords]
      newKeywords[index] = {
        value: '',
        isCustom: true
      }
      setKeywords(newKeywords)
    }
  }

  // カスタムキーワードの更新
  const updateCustomKeyword = (index: number, value: string) => {
    const newKeywords = [...keywords]
    newKeywords[index] = {
      value,
      isCustom: true
    }
    setKeywords(newKeywords)
  }

  // 入力値の統合
  const prepareInput = () => {
    const finalKeywords = keywords
      .filter(k => k.value)
      .map(k => k.value)

    const preparedInput = {
      ...input,
      name: nameMode === 'custom' && input.name ? input.name : undefined,
      age: ageMode === 'custom' && input.age ? input.age : undefined,
      role: roleMode === 'custom' ? customRole : selectedRole,
      keywords: finalKeywords.length > 0 ? finalKeywords : undefined,
      additionalSettings: additionalSettings.trim() || undefined,
      relatedCharacterContext: input.relatedCharacterContext,
      relationshipType: input.relationshipType
    }

    // デバッグ: 送信される名前データを確認
    console.log('[CharacterForm] Mode:', input.mode)
    console.log('[CharacterForm] Name mode:', nameMode)
    console.log('[CharacterForm] Input name:', input.name)
    console.log('[CharacterForm] Prepared name:', preparedInput.name)
    console.log('[CharacterForm] Seed:', preparedInput.seed)
    console.log('[CharacterForm] Full prepared input:', preparedInput)

    onInputChange(preparedInput)
    return preparedInput
  }

  return (
    <div className="space-y-6">
      {/* 部分再生成モードの表示 */}
      {partialRegenerateMode && existingResult && (
        <div className="card bg-muted/50 border-2 border-primary/20">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">部分再生成モード</h3>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (onPartialRegenerateModeChange) {
                  onPartialRegenerateModeChange(false)
                }
              }}
            >
              通常モードに戻る
            </Button>
          </div>
          
          <p className="text-sm text-muted-foreground mb-4">
            読み込んだキャラクター「{existingResult.name}」の部分的な再生成を行います。
            ロックしたセクションは保持され、ロックされていない部分のみ再生成されます。
          </p>
          
          <div className="space-y-4">
            <Label>再生成する項目を選択（ロック = 保持、アンロック = 再生成）:</Label>
            
            {/* タブで項目をグループ化 */}
            <Tabs defaultValue="personality" className="w-full">
              <TabsList className="flex flex-wrap gap-1 h-auto p-1 w-full">
                <TabsTrigger value="personality" className="text-xs">性格</TabsTrigger>
                <TabsTrigger value="background" className="text-xs">背景</TabsTrigger>
                <TabsTrigger value="appearance" className="text-xs">外見</TabsTrigger>
                <TabsTrigger value="behavior" className="text-xs">行動</TabsTrigger>
                <TabsTrigger value="development" className="text-xs">成長</TabsTrigger>
                <TabsTrigger value="emotional" className="text-xs">感情</TabsTrigger>
                <TabsTrigger value="interpersonal" className="text-xs">対人</TabsTrigger>
                <TabsTrigger value="thinking" className="text-xs">思考</TabsTrigger>
                <TabsTrigger value="daily" className="text-xs">日常</TabsTrigger>
                <TabsTrigger value="beliefs" className="text-xs">信念</TabsTrigger>
                <TabsTrigger value="expertise" className="text-xs">専門</TabsTrigger>
                <TabsTrigger value="relations" className="text-xs">関係</TabsTrigger>
                <TabsTrigger value="dialogues" className="text-xs">台詞</TabsTrigger>
              </TabsList>
              
              {/* 性格タブ */}
              <TabsContent value="personality" className="space-y-2">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    { key: 'traits', label: '性格特性' },
                    { key: 'strengths', label: '長所' },
                    { key: 'weaknesses', label: '短所・弱点' },
                    { key: 'fears', label: '恐れ' },
                    { key: 'desires', label: '欲求' },
                    { key: 'values', label: '価値観' }
                  ].map(item => {
                    const isLocked = typeof lockedSections.personality === 'object' 
                      ? lockedSections.personality[item.key as keyof typeof lockedSections.personality]
                      : lockedSections.personality
                    return (
                      <Button
                        key={item.key}
                        variant={isLocked ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          if (onLockedSectionsChange) {
                            const currentPersonality = typeof lockedSections.personality === 'object' 
                              ? lockedSections.personality 
                              : {}
                            onLockedSectionsChange({
                              ...lockedSections,
                              personality: {
                                ...currentPersonality,
                                [item.key]: !isLocked
                              }
                            })
                          }
                        }}
                        className="justify-start"
                      >
                        {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                        {item.label}
                      </Button>
                    )
                  })}
                </div>
              </TabsContent>
              
              {/* 背景タブ */}
              <TabsContent value="background" className="space-y-2">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    { key: 'birthplace', label: '出身地' },
                    { key: 'family', label: '家族構成' },
                    { key: 'education', label: '教育・訓練' },
                    { key: 'occupation', label: '職業・立場' },
                    { key: 'significantEvents', label: '重要な出来事' },
                    { key: 'relationships', label: '人間関係' },
                    { key: 'secrets', label: '秘密' },
                    { key: 'abilities', label: '能力・スキル' }
                  ].map(item => {
                    const isLocked = typeof lockedSections.background === 'object' 
                      ? lockedSections.background[item.key as keyof typeof lockedSections.background]
                      : lockedSections.background
                    return (
                      <Button
                        key={item.key}
                        variant={isLocked ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          if (onLockedSectionsChange) {
                            const currentBackground = typeof lockedSections.background === 'object' 
                              ? lockedSections.background 
                              : {}
                            onLockedSectionsChange({
                              ...lockedSections,
                              background: {
                                ...currentBackground,
                                [item.key]: !isLocked
                              }
                            })
                          }
                        }}
                        className="justify-start"
                      >
                        {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                        {item.label}
                      </Button>
                    )
                  })}
                </div>
              </TabsContent>
              
              {/* 外見タブ */}
              <TabsContent value="appearance" className="space-y-2">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    { key: 'height', label: '身長' },
                    { key: 'build', label: '体格' },
                    { key: 'hairColor', label: '髪色' },
                    { key: 'hairStyle', label: '髪型' },
                    { key: 'eyeColor', label: '瞳の色' },
                    { key: 'facialFeatures', label: '顔立ち' },
                    { key: 'clothing', label: '服装' },
                    { key: 'distinctiveMarks', label: '特徴的な印' },
                    { key: 'overallImpression', label: '全体的印象' }
                  ].map(item => {
                    const isLocked = typeof lockedSections.appearance === 'object' 
                      ? lockedSections.appearance[item.key as keyof typeof lockedSections.appearance]
                      : lockedSections.appearance
                    return (
                      <Button
                        key={item.key}
                        variant={isLocked ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          if (onLockedSectionsChange) {
                            const currentAppearance = typeof lockedSections.appearance === 'object' 
                              ? lockedSections.appearance 
                              : {}
                            onLockedSectionsChange({
                              ...lockedSections,
                              appearance: {
                                ...currentAppearance,
                                [item.key]: !isLocked
                              }
                            })
                          }
                        }}
                        className="justify-start"
                      >
                        {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                        {item.label}
                      </Button>
                    )
                  })}
                </div>
              </TabsContent>
              
              {/* 行動タブ */}
              <TabsContent value="behavior" className="space-y-2">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    { key: 'firstPerson', label: '一人称' },
                    { key: 'speechPattern', label: '話し方' },
                    { key: 'mannerisms', label: '癖・仕草' },
                    { key: 'habits', label: '習慣' },
                    { key: 'socialStyle', label: '対人スタイル' },
                    { key: 'decisionMaking', label: '意思決定' },
                    { key: 'stressResponse', label: 'ストレス反応' },
                    { key: 'conflictResolution', label: '対立への対処' },
                    { key: 'combatStyle', label: '武器・戦闘スタイル' }
                  ].map(item => {
                    const isLocked = typeof lockedSections.behavior === 'object' 
                      ? lockedSections.behavior[item.key as keyof typeof lockedSections.behavior]
                      : lockedSections.behavior
                    return (
                      <Button
                        key={item.key}
                        variant={isLocked ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          if (onLockedSectionsChange) {
                            const currentBehavior = typeof lockedSections.behavior === 'object' 
                              ? lockedSections.behavior 
                              : {}
                            onLockedSectionsChange({
                              ...lockedSections,
                              behavior: {
                                ...currentBehavior,
                                [item.key]: !isLocked
                              }
                            })
                          }
                        }}
                        className="justify-start"
                      >
                        {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                        {item.label}
                      </Button>
                    )
                  })}
                </div>
              </TabsContent>
              
              {/* 成長タブ */}
              <TabsContent value="development" className="space-y-2">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    { key: 'currentState', label: '現在の状態' },
                    { key: 'growthPotential', label: '成長の可能性' },
                    { key: 'internalConflicts', label: '内的葛藤' },
                    { key: 'externalChallenges', label: '外的課題' },
                    { key: 'arcSuggestions', label: '物語の展開案' }
                  ].map(item => {
                    const isLocked = typeof lockedSections.development === 'object' 
                      ? lockedSections.development[item.key as keyof typeof lockedSections.development]
                      : lockedSections.development
                    return (
                      <Button
                        key={item.key}
                        variant={isLocked ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          if (onLockedSectionsChange) {
                            const currentDevelopment = typeof lockedSections.development === 'object' 
                              ? lockedSections.development 
                              : {}
                            onLockedSectionsChange({
                              ...lockedSections,
                              development: {
                                ...currentDevelopment,
                                [item.key]: !isLocked
                              }
                            })
                          }
                        }}
                        className="justify-start"
                      >
                        {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                        {item.label}
                      </Button>
                    )
                  })}
                </div>
              </TabsContent>

              {/* 感情表現タブ */}
              <TabsContent value="emotional" className="space-y-2">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    { key: 'joy', label: '喜び' },
                    { key: 'anger', label: '怒り' },
                    { key: 'sadness', label: '哀しみ' },
                    { key: 'enjoyment', label: '楽しみ' },
                    { key: 'emotionalControl', label: '感情抑制' },
                    { key: 'stressRelief', label: 'ストレス発散' },
                    { key: 'cryingStyle', label: '泣き方' },
                    { key: 'laughingStyle', label: '笑い方' }
                  ].map(item => {
                    const isLocked = typeof lockedSections.emotionalExpression === 'object'
                      ? lockedSections.emotionalExpression?.[item.key as keyof typeof lockedSections.emotionalExpression]
                      : lockedSections.emotionalExpression
                    return (
                      <Button
                        key={item.key}
                        variant={isLocked ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          if (onLockedSectionsChange) {
                            const current = typeof lockedSections.emotionalExpression === 'object'
                              ? lockedSections.emotionalExpression : {}
                            onLockedSectionsChange({
                              ...lockedSections,
                              emotionalExpression: { ...current, [item.key]: !isLocked }
                            })
                          }
                        }}
                        className="justify-start"
                      >
                        {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                        {item.label}
                      </Button>
                    )
                  })}
                </div>
              </TabsContent>

              {/* 対人関係タブ */}
              <TabsContent value="interpersonal" className="space-y-2">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    { key: 'firstMeetingBehavior', label: '初対面' },
                    { key: 'trustCriteria', label: '信頼基準' },
                    { key: 'distanceCriteria', label: '距離基準' },
                    { key: 'romanticView', label: '恋愛観' },
                    { key: 'hierarchyBehavior', label: '上下関係' },
                    { key: 'groupPosition', label: '集団立場' }
                  ].map(item => {
                    const isLocked = typeof lockedSections.interpersonalPatterns === 'object'
                      ? lockedSections.interpersonalPatterns?.[item.key as keyof typeof lockedSections.interpersonalPatterns]
                      : lockedSections.interpersonalPatterns
                    return (
                      <Button
                        key={item.key}
                        variant={isLocked ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          if (onLockedSectionsChange) {
                            const current = typeof lockedSections.interpersonalPatterns === 'object'
                              ? lockedSections.interpersonalPatterns : {}
                            onLockedSectionsChange({
                              ...lockedSections,
                              interpersonalPatterns: { ...current, [item.key]: !isLocked }
                            })
                          }
                        }}
                        className="justify-start"
                      >
                        {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                        {item.label}
                      </Button>
                    )
                  })}
                </div>
              </TabsContent>

              {/* 思考プロセスタブ */}
              <TabsContent value="thinking" className="space-y-2">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    { key: 'problemSolvingApproach', label: '問題解決' },
                    { key: 'crisisJudgment', label: '危機判断' },
                    { key: 'prioritization', label: '優先順位' },
                    { key: 'planningAbility', label: '計画性' },
                    { key: 'intuitionLogicBalance', label: '直感/論理' }
                  ].map(item => {
                    const isLocked = typeof lockedSections.thinkingProcess === 'object'
                      ? lockedSections.thinkingProcess?.[item.key as keyof typeof lockedSections.thinkingProcess]
                      : lockedSections.thinkingProcess
                    return (
                      <Button
                        key={item.key}
                        variant={isLocked ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          if (onLockedSectionsChange) {
                            const current = typeof lockedSections.thinkingProcess === 'object'
                              ? lockedSections.thinkingProcess : {}
                            onLockedSectionsChange({
                              ...lockedSections,
                              thinkingProcess: { ...current, [item.key]: !isLocked }
                            })
                          }
                        }}
                        className="justify-start"
                      >
                        {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                        {item.label}
                      </Button>
                    )
                  })}
                </div>
              </TabsContent>

              {/* 日常生活タブ */}
              <TabsContent value="daily" className="space-y-2">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    { key: 'routine', label: '日常描写' },
                    { key: 'importantItems', label: '重要アイテム' }
                  ].map(item => {
                    const isLocked = typeof lockedSections.dailyLife === 'object'
                      ? lockedSections.dailyLife?.[item.key as keyof typeof lockedSections.dailyLife]
                      : lockedSections.dailyLife
                    return (
                      <Button
                        key={item.key}
                        variant={isLocked ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          if (onLockedSectionsChange) {
                            const current = typeof lockedSections.dailyLife === 'object'
                              ? lockedSections.dailyLife : {}
                            onLockedSectionsChange({
                              ...lockedSections,
                              dailyLife: { ...current, [item.key]: !isLocked }
                            })
                          }
                        }}
                        className="justify-start"
                      >
                        {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                        {item.label}
                      </Button>
                    )
                  })}
                </div>
              </TabsContent>

              {/* 信念タブ */}
              <TabsContent value="beliefs" className="space-y-2">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    { key: 'lifePhilosophy', label: '人生哲学' },
                    { key: 'workBeliefs', label: '仕事信念' },
                    { key: 'worldview', label: '世界観' },
                    { key: 'coreValues', label: '価値観' },
                    { key: 'faith', label: '信仰' }
                  ].map(item => {
                    const isLocked = typeof lockedSections.beliefs === 'object'
                      ? lockedSections.beliefs?.[item.key as keyof typeof lockedSections.beliefs]
                      : lockedSections.beliefs
                    return (
                      <Button
                        key={item.key}
                        variant={isLocked ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          if (onLockedSectionsChange) {
                            const current = typeof lockedSections.beliefs === 'object'
                              ? lockedSections.beliefs : {}
                            onLockedSectionsChange({
                              ...lockedSections,
                              beliefs: { ...current, [item.key]: !isLocked }
                            })
                          }
                        }}
                        className="justify-start"
                      >
                        {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                        {item.label}
                      </Button>
                    )
                  })}
                </div>
              </TabsContent>

              {/* 専門性タブ */}
              <TabsContent value="expertise" className="space-y-2">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    { key: 'mainField', label: '専門分野' },
                    { key: 'practicalSkills', label: '実務能力' },
                    { key: 'intellectualWorks', label: '知的活動' },
                    { key: 'characteristicEpisodes', label: 'エピソード' }
                  ].map(item => {
                    const isLocked = typeof lockedSections.expertise === 'object'
                      ? lockedSections.expertise?.[item.key as keyof typeof lockedSections.expertise]
                      : lockedSections.expertise
                    return (
                      <Button
                        key={item.key}
                        variant={isLocked ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          if (onLockedSectionsChange) {
                            const current = typeof lockedSections.expertise === 'object'
                              ? lockedSections.expertise : {}
                            onLockedSectionsChange({
                              ...lockedSections,
                              expertise: { ...current, [item.key]: !isLocked }
                            })
                          }
                        }}
                        className="justify-start"
                      >
                        {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                        {item.label}
                      </Button>
                    )
                  })}
                </div>
              </TabsContent>

              {/* 関係性詳細タブ */}
              <TabsContent value="relations" className="space-y-2">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    { key: 'nicknames', label: '呼び方' },
                    { key: 'respectedPeople', label: '尊敬する人' },
                    { key: 'rivals', label: 'ライバル' }
                  ].map(item => {
                    const isLocked = typeof lockedSections.relationshipDetails === 'object'
                      ? lockedSections.relationshipDetails?.[item.key as keyof typeof lockedSections.relationshipDetails]
                      : lockedSections.relationshipDetails
                    return (
                      <Button
                        key={item.key}
                        variant={isLocked ? "default" : "outline"}
                        size="sm"
                        onClick={() => {
                          if (onLockedSectionsChange) {
                            const current = typeof lockedSections.relationshipDetails === 'object'
                              ? lockedSections.relationshipDetails : {}
                            onLockedSectionsChange({
                              ...lockedSections,
                              relationshipDetails: { ...current, [item.key]: !isLocked }
                            })
                          }
                        }}
                        className="justify-start"
                      >
                        {isLocked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
                        {item.label}
                      </Button>
                    )
                  })}
                </div>
              </TabsContent>

              {/* サンプル台詞タブ */}
              <TabsContent value="dialogues" className="space-y-2">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">サンプル台詞は個別にロック可能です</p>
                  {existingResult?.sampleDialogues && existingResult.sampleDialogues.length > 0 && (
                    <div className="grid grid-cols-1 gap-2">
                      {existingResult.sampleDialogues.map((dialogue, index) => {
                        const isLocked = Array.isArray(lockedSections.sampleDialogues) 
                          ? lockedSections.sampleDialogues.includes(index)
                          : lockedSections.sampleDialogues
                        return (
                          <Button
                            key={index}
                            variant={isLocked ? "default" : "outline"}
                            size="sm"
                            onClick={() => {
                              if (onLockedSectionsChange) {
                                let newSampleDialogues: boolean | number[]
                                if (Array.isArray(lockedSections.sampleDialogues)) {
                                  if (isLocked) {
                                    newSampleDialogues = lockedSections.sampleDialogues.filter(i => i !== index)
                                  } else {
                                    newSampleDialogues = [...lockedSections.sampleDialogues, index]
                                  }
                                } else {
                                  newSampleDialogues = isLocked ? [] : [index]
                                }
                                onLockedSectionsChange({
                                  ...lockedSections,
                                  sampleDialogues: newSampleDialogues
                                })
                              }
                            }}
                            className="justify-start text-left"
                          >
                            {isLocked ? <Lock className="h-3 w-3 mr-1 flex-shrink-0" /> : <Unlock className="h-3 w-3 mr-1 flex-shrink-0" />}
                            <span className="truncate">
                              台詞{index + 1}: {dialogue.situation}
                            </span>
                          </Button>
                        )
                      })}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
            
            {/* 一括操作ボタン */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (onLockedSectionsChange) {
                    onLockedSectionsChange({})
                  }
                }}
              >
                すべてアンロック（再生成）
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (onLockedSectionsChange && existingResult) {
                    // すべての項目をロック
                    onLockedSectionsChange({
                      personality: {
                        traits: true,
                        strengths: true,
                        weaknesses: true,
                        fears: true,
                        desires: true,
                        values: true
                      },
                      background: {
                        birthplace: true,
                        family: true,
                        education: true,
                        occupation: true,
                        significantEvents: true,
                        relationships: true,
                        secrets: true,
                        abilities: true
                      },
                      appearance: {
                        height: true,
                        build: true,
                        hairColor: true,
                        hairStyle: true,
                        eyeColor: true,
                        facialFeatures: true,
                        clothing: true,
                        distinctiveMarks: true,
                        overallImpression: true
                      },
                      behavior: {
                        firstPerson: true,
                        speechPattern: true,
                        mannerisms: true,
                        habits: true,
                        socialStyle: true,
                        decisionMaking: true,
                        stressResponse: true,
                        conflictResolution: true,
                        combatStyle: true
                      },
                      development: {
                        currentState: true,
                        growthPotential: true,
                        internalConflicts: true,
                        externalChallenges: true,
                        arcSuggestions: true
                      },
                      emotionalExpression: {
                        joy: true,
                        anger: true,
                        sadness: true,
                        enjoyment: true,
                        emotionalControl: true,
                        stressRelief: true,
                        cryingStyle: true,
                        laughingStyle: true
                      },
                      interpersonalPatterns: {
                        firstMeetingBehavior: true,
                        trustCriteria: true,
                        distanceCriteria: true,
                        romanticView: true,
                        hierarchyBehavior: true,
                        groupPosition: true
                      },
                      thinkingProcess: {
                        problemSolvingApproach: true,
                        crisisJudgment: true,
                        prioritization: true,
                        planningAbility: true,
                        intuitionLogicBalance: true
                      },
                      dailyLife: {
                        routine: true,
                        importantItems: true
                      },
                      beliefs: {
                        lifePhilosophy: true,
                        workBeliefs: true,
                        worldview: true,
                        coreValues: true,
                        faith: true
                      },
                      expertise: {
                        mainField: true,
                        practicalSkills: true,
                        intellectualWorks: true,
                        characteristicEpisodes: true
                      },
                      relationshipDetails: {
                        nicknames: true,
                        respectedPeople: true,
                        rivals: true
                      },
                      sampleDialogues: existingResult.sampleDialogues.map((_, i) => i)
                    })
                  }
                }}
              >
                すべてロック（保持）
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 情報更新モードの表示 */}
      {updateMode && existingResult && (
        <div className="card bg-muted/50 border-2 border-blue-500/20">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-blue-500" />
              <h3 className="font-semibold">情報更新モード</h3>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (onUpdateModeChange) {
                  onUpdateModeChange(false)
                }
              }}
            >
              キャンセル
            </Button>
          </div>

          <p className="text-sm text-muted-foreground mb-4">
            「{existingResult.name}」の情報を更新します。
            下記に更新内容を入力し、AIが既存データとマージします。
          </p>

          <div className="space-y-4">
            <div>
              <Label>更新情報を入力</Label>
              <Textarea
                value={updateSource}
                onChange={(e) => onUpdateSourceChange?.(e.target.value)}
                placeholder="例：&#10;・第5話で左腕を失った&#10;・魔王との戦いで覚醒し、新たな能力「光の剣」を得た&#10;・ヒロインとの関係が恋人に進展&#10;・口癖が「やれやれ」から「任せろ」に変化"
                className="min-h-[200px] resize-y"
              />
              <p className="text-xs text-muted-foreground mt-2">
                変更点や追加情報を自由に記述してください。既存データと異なる部分は更新され、新情報は追加されます。
              </p>
            </div>

            <div>
              <Label>または参考ファイルをアップロード</Label>
              <div className="mt-2">
                <input
                  type="file"
                  accept=".txt,.md,.json"
                  onChange={async (e) => {
                    const file = e.target.files?.[0]
                    if (file) {
                      const text = await file.text()
                      onUpdateSourceChange?.((updateSource ? updateSource + '\n\n' : '') + text)
                    }
                    e.target.value = ''
                  }}
                  className="text-sm"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  テキスト/Markdown/JSONファイルをアップロードして内容を追加できます
                </p>
              </div>
            </div>

            {/* データ連携セクション */}
            <div className="border-t pt-4 space-y-3">
              <Label className="text-sm font-medium">データ連携（オプション）</Label>
              <p className="text-xs text-muted-foreground">更新時に関連データを参照することで、より整合性のある更新が可能です</p>

              {/* 世界観 */}
              {savedWorlds.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs">世界観</Label>
                  <Select
                    value={selectedWorldId || 'none'}
                    onValueChange={(value) => {
                      if (value === 'none') {
                        setSelectedWorldId('')
                        onInputChange({ ...input, worldContext: undefined })
                      } else {
                        setSelectedWorldId(value)
                        const world = savedWorlds.find(w => w.id === value)
                        if (world) {
                          onInputChange({ ...input, worldContext: generateWorldContextText(world.result) })
                        }
                      }
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="世界観を選択" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">選択なし</SelectItem>
                      {savedWorlds.map(world => (
                        <SelectItem key={world.id} value={world.id}>{world.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* 関連キャラクター */}
              {characters.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs">関連キャラクター</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCharacterSelector(!showCharacterSelector)}
                    className="w-full h-8 text-xs justify-between"
                  >
                    <span>{selectedCharacterIds.length > 0 ? `${selectedCharacterIds.length}人選択中` : 'キャラクターを選択'}</span>
                    {showCharacterSelector ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </Button>
                  {showCharacterSelector && (
                    <div className="border rounded p-2 max-h-[150px] overflow-y-auto space-y-1">
                      {characters.filter(c => c.id !== existingResult?.name).map(char => (
                        <div key={char.id} className="flex items-center gap-2 text-xs">
                          <Checkbox
                            checked={selectedCharacterIds.includes(char.id)}
                            onCheckedChange={(checked) => {
                              const newIds = checked ? [...selectedCharacterIds, char.id] : selectedCharacterIds.filter(id => id !== char.id)
                              setSelectedCharacterIds(newIds)
                              const selectedChars = newIds.map(id => characters.find(c => c.id === id)).filter(Boolean)
                              onInputChange({
                                ...input,
                                relatedCharacterIds: newIds,
                                relatedCharactersContext: JSON.stringify(selectedChars.map(c => c?.result)),
                                characterRelationships
                              })
                            }}
                          />
                          <span className="truncate">{char.result.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* コミュニティ */}
              {savedCommunities.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs">関連コミュニティ</Label>
                  <Select
                    value={selectedCommunityId || 'none'}
                    onValueChange={(value) => {
                      if (value === 'none') {
                        setSelectedCommunityId('')
                        onInputChange({ ...input, communityId: undefined, communityContext: undefined })
                      } else {
                        setSelectedCommunityId(value)
                        const community = savedCommunities.find(c => c.id === value)
                        if (community) {
                          onInputChange({ ...input, communityId: value, communityContext: JSON.stringify(community.result) })
                        }
                      }
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="コミュニティを選択" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">選択なし</SelectItem>
                      {savedCommunities.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.result.basicInfo.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* ソサエティ */}
              {savedSocieties.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs">関連組織</Label>
                  <Select
                    value={selectedSocietyId || 'none'}
                    onValueChange={(value) => {
                      if (value === 'none') {
                        setSelectedSocietyId('')
                        onInputChange({ ...input, societyId: undefined, societyContext: undefined })
                      } else {
                        setSelectedSocietyId(value)
                        const society = savedSocieties.find(s => s.id === value)
                        if (society) {
                          onInputChange({ ...input, societyId: value, societyContext: JSON.stringify(society.result) })
                        }
                      }
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="組織を選択" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">選択なし</SelectItem>
                      {savedSocieties.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.result.basicInfo?.name || s.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* アイテム */}
              {savedItems.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs">関連アイテム</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowItemSelector(!showItemSelector)}
                    className="w-full h-8 text-xs justify-between"
                  >
                    <span>{selectedItemIds.length > 0 ? `${selectedItemIds.length}個選択中` : 'アイテムを選択'}</span>
                    {showItemSelector ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </Button>
                  {showItemSelector && (
                    <div className="border rounded p-2 max-h-[150px] overflow-y-auto space-y-1">
                      {savedItems.map(item => (
                        <div key={item.id} className="flex items-center gap-2 text-xs">
                          <Checkbox
                            checked={selectedItemIds.includes(item.id)}
                            onCheckedChange={(checked) => {
                              const newIds = checked ? [...selectedItemIds, item.id] : selectedItemIds.filter(id => id !== item.id)
                              setSelectedItemIds(newIds)
                              const selectedItems = newIds.map(id => savedItems.find(i => i.id === id)).filter(Boolean)
                              onInputChange({
                                ...input,
                                itemIds: newIds,
                                itemsContext: JSON.stringify(selectedItems.map(i => i?.result)),
                                itemRelationships
                              })
                            }}
                          />
                          <span className="truncate">{item.result.basicInfo.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* スキル */}
              {savedSkills.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs">関連スキル</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSkillSelector(!showSkillSelector)}
                    className="w-full h-8 text-xs justify-between"
                  >
                    <span>{selectedSkillIds.length > 0 ? `${selectedSkillIds.length}個選択中` : 'スキルを選択'}</span>
                    {showSkillSelector ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </Button>
                  {showSkillSelector && (
                    <div className="border rounded p-2 max-h-[150px] overflow-y-auto space-y-1">
                      {savedSkills.map(skill => (
                        <div key={skill.id} className="flex items-center gap-2 text-xs">
                          <Checkbox
                            checked={selectedSkillIds.includes(skill.id)}
                            onCheckedChange={(checked) => {
                              const newIds = checked ? [...selectedSkillIds, skill.id] : selectedSkillIds.filter(id => id !== skill.id)
                              setSelectedSkillIds(newIds)
                              const selectedSkills = newIds.map(id => savedSkills.find(s => s.id === id)).filter(Boolean)
                              onInputChange({
                                ...input,
                                skillIds: newIds,
                                skillsContext: JSON.stringify(selectedSkills.map(s => s?.result)),
                                skillRelationships
                              })
                            }}
                          />
                          <span className="truncate">{skill.result.basicInfo.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                <ul className="text-xs space-y-1 mt-1">
                  <li>・既存情報と矛盾する内容は新しい情報で上書きされます</li>
                  <li>・新規情報は適切なフィールドに追加されます</li>
                  <li>・サンプル台詞は既存のものに追加されます（上限なし）</li>
                  <li>・更新後は新規保存または上書き保存が選択できます</li>
                </ul>
              </AlertDescription>
            </Alert>
          </div>
        </div>
      )}

      {/* 更新モード中は通常フォームを非表示 */}
      {!updateMode && (
      <Tabs value={input.mode || 'standard'} onValueChange={(value) => handleModeChange(value as 'standard' | 'idea')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="standard">標準モード</TabsTrigger>
          <TabsTrigger value="idea">アイディアモード</TabsTrigger>
        </TabsList>

        <TabsContent value="standard" className="space-y-6">
          {/* 基本情報 */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">基本情報</h3>
            
            {/* 名前 */}
            <div className="space-y-2">
              <Label>キャラクター名</Label>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={nameMode === 'auto' ? 'default' : 'outline'}
                  onClick={() => setNameMode('auto')}
                >
                  お任せ
                </Button>
                <Button
                  size="sm"
                  variant={nameMode === 'custom' ? 'default' : 'outline'}
                  onClick={() => setNameMode('custom')}
                >
                  指定する
                </Button>
              </div>
              {nameMode === 'custom' && (
                <Input
                  value={input.name || ''}
                  onChange={(e) => onInputChange({ ...input, name: e.target.value })}
                  placeholder="例：山田太郎（空欄の場合はお任せ）"
                />
              )}
            </div>

            {/* 年齢 */}
            <div className="space-y-2">
              <Label>年齢</Label>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={ageMode === 'auto' ? 'default' : 'outline'}
                  onClick={() => setAgeMode('auto')}
                >
                  お任せ
                </Button>
                <Button
                  size="sm"
                  variant={ageMode === 'custom' ? 'default' : 'outline'}
                  onClick={() => setAgeMode('custom')}
                >
                  指定する
                </Button>
              </div>
              {ageMode === 'custom' && (
                <Input
                  value={input.age || ''}
                  onChange={(e) => onInputChange({ ...input, age: e.target.value })}
                  placeholder="例：25歳、約30歳（空欄の場合はお任せ）"
                />
              )}
            </div>

            {/* 役割 */}
            <div className="space-y-2">
              <Label>役割</Label>
              <div className="flex gap-2 mb-2">
                <Button
                  size="sm"
                  variant={roleMode === 'select' ? 'default' : 'outline'}
                  onClick={() => setRoleMode('select')}
                >
                  選択
                </Button>
                <Button
                  size="sm"
                  variant={roleMode === 'custom' ? 'default' : 'outline'}
                  onClick={() => setRoleMode('custom')}
                >
                  自由入力
                </Button>
              </div>
              {roleMode === 'select' ? (
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={customRole}
                  onChange={(e) => setCustomRole(e.target.value)}
                  placeholder="役割を自由に入力"
                />
              )}
            </div>

            {/* 性別 */}
            <div>
              <Label htmlFor="gender">性別</Label>
              <Select value={input.gender || ''} onValueChange={(value) => onInputChange({ ...input, gender: value as any })}>
                <SelectTrigger>
                  <SelectValue placeholder="選択してください" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">男性</SelectItem>
                  <SelectItem value="female">女性</SelectItem>
                  <SelectItem value="other">その他</SelectItem>
                  <SelectItem value="unspecified">未設定</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* ジャンル */}
            <div>
              <Label htmlFor="genre">ジャンル</Label>
              <Select value={input.genre || ''} onValueChange={(value) => onInputChange({ ...input, genre: value as any })}>
                <SelectTrigger>
                  <SelectValue placeholder="選択してください" />
                </SelectTrigger>
                <SelectContent>
                  {GENRE_OPTIONS.map(genre => (
                    <SelectItem key={genre.value} value={genre.value}>
                      {genre.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* 世界観選択 */}
            {savedWorlds.length > 0 && (
              <div>
                <Label htmlFor="world">世界観（オプション）</Label>
                <Select 
                  value={selectedWorldId || 'none'} 
                  onValueChange={(value) => {
                    if (value === 'none') {
                      setSelectedWorldId('')
                      onInputChange({ ...input, worldContext: undefined })
                    } else {
                      setSelectedWorldId(value)
                      const world = savedWorlds.find(w => w.id === value)
                      if (world) {
                        const worldContext = generateWorldContextText(world.result)
                        onInputChange({ ...input, worldContext })
                      }
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="世界観を選択...">
                      {selectedWorldId ? (
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4" />
                          {savedWorlds.find(w => w.id === selectedWorldId)?.title}
                        </div>
                      ) : (
                        '世界観を選択...'
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">選択しない</SelectItem>
                    {savedWorlds.map((world) => (
                      <SelectItem key={world.id} value={world.id}>
                        <div className="flex flex-col">
                          <div className="font-medium">{world.title}</div>
                          <div className="text-xs text-muted-foreground">
                            {world.result.summary?.substring(0, 50)}...
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedWorldId && (
                  <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                    <div className="font-medium text-blue-900 mb-1">選択中の世界観</div>
                    <div className="text-blue-700">
                      {savedWorlds.find(w => w.id === selectedWorldId)?.result.summary?.substring(0, 100)}...
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* 関連キャラクター選択（複数対応） */}
            {characters.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>関連キャラクター（オプション）</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowCharacterSelector(!showCharacterSelector)}
                  >
                    {showCharacterSelector ? (
                      <>
                        <ChevronUp className="h-4 w-4 mr-1" />
                        閉じる
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4 mr-1" />
                        開く ({selectedCharacterIds.length}/{characters.length}人選択中)
                      </>
                    )}
                  </Button>
                </div>
                
                {showCharacterSelector && (
                  <>
                    {/* 検索バー */}
                    {characters.length > 5 && (
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="text"
                          placeholder="名前、役割、年齢などで検索..."
                          value={characterSearchQuery}
                          onChange={(e) => setCharacterSearchQuery(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                    )}
                    
                    {/* キャラクターリスト */}
                    <div className="p-3 border rounded-lg space-y-2 max-h-48 overflow-y-auto">
                      {(() => {
                        const filteredCharacters = characters.filter(character => {
                          if (!characterSearchQuery) return true
                          const query = characterSearchQuery.toLowerCase()
                          return (
                            character.result.name.toLowerCase().includes(query) ||
                            character.result.role.toLowerCase().includes(query) ||
                            character.result.age?.toLowerCase().includes(query) ||
                            character.result.summary?.toLowerCase().includes(query)
                          )
                        })
                        
                        if (filteredCharacters.length === 0) {
                          return (
                            <p className="text-sm text-muted-foreground text-center py-2">
                              該当するキャラクターが見つかりません
                            </p>
                          )
                        }
                        
                        return filteredCharacters.map(character => (
                          <div key={character.id} className="flex items-start gap-2">
                            <Checkbox
                              id={`char-${character.id}`}
                              checked={selectedCharacterIds.includes(character.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedCharacterIds([...selectedCharacterIds, character.id])
                                } else {
                                  setSelectedCharacterIds(selectedCharacterIds.filter(id => id !== character.id))
                                  // 関係性も削除
                                  const newRelationships = { ...characterRelationships }
                                  delete newRelationships[character.id]
                                  setCharacterRelationships(newRelationships)
                                }
                                
                                // inputを更新
                                const newIds = checked 
                                  ? [...selectedCharacterIds, character.id]
                                  : selectedCharacterIds.filter(id => id !== character.id)
                                
                                if (newIds.length > 0) {
                                  const selectedChars = newIds.map(id => characters.find(c => c.id === id)).filter(Boolean)
                                  const contextsJson = JSON.stringify(selectedChars.map(c => c.result))
                                  onInputChange({ 
                                    ...input, 
                                    relatedCharacterIds: newIds,
                                    relatedCharactersContext: contextsJson,
                                    characterRelationships: characterRelationships
                                  })
                                } else {
                                  onInputChange({ 
                                    ...input, 
                                    relatedCharacterIds: undefined,
                                    relatedCharactersContext: undefined,
                                    characterRelationships: undefined
                                  })
                                }
                              }}
                            />
                            <label 
                              htmlFor={`char-${character.id}`} 
                              className="flex-1 cursor-pointer text-sm"
                            >
                              <div className="font-medium">{character.result.name}</div>
                              <div className="text-xs text-muted-foreground">
                                {character.result.role} / {character.result.age} / {character.result.gender}
                              </div>
                            </label>
                          </div>
                        ))
                      })()}
                    </div>
                    
                    {/* 選択されたキャラクターとの関係性入力 */}
                    {selectedCharacterIds.length > 0 && (
                      <div className="space-y-2 mt-3">
                        <Label>各キャラクターとの関係性</Label>
                        {selectedCharacterIds.map(charId => {
                          const character = characters.find(c => c.id === charId)
                          if (!character) return null
                          
                          return (
                            <div key={charId} className="flex items-center gap-2">
                              <span className="text-sm w-24 truncate" title={character.result.name}>
                                {character.result.name}:
                              </span>
                              <Input
                                value={characterRelationships[charId] || ''}
                                onChange={(e) => {
                                  const newRelationships = {
                                    ...characterRelationships,
                                    [charId]: e.target.value
                                  }
                                  setCharacterRelationships(newRelationships)
                                  onInputChange({ 
                                    ...input, 
                                    characterRelationships: newRelationships
                                  })
                                }}
                                placeholder="例：兄弟、恋人、ライバル、師弟など"
                                className="flex-1"
                              />
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </>
                )}
                
                {/* 選択されたキャラクターのサマリー */}
                {selectedCharacterIds.length > 0 && (
                  <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                    <div className="font-medium text-blue-900 mb-1">
                      選択中のキャラクター ({selectedCharacterIds.length}人)
                    </div>
                    <div className="text-blue-700 space-y-1">
                      {selectedCharacterIds.slice(0, 3).map(id => {
                        const character = characters.find(c => c.id === id)
                        if (!character) return null
                        return (
                          <div key={id}>
                            {character.result.name}: {character.result.summary?.substring(0, 50)}...
                          </div>
                        )
                      })}
                      {selectedCharacterIds.length > 3 && (
                        <div>他 {selectedCharacterIds.length - 3} 人...</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* コミュニティ選択 */}
            {savedCommunities.length > 0 && (
              <div>
                <Alert className="mb-3">
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    キャラクターが所属または関連する都市・国・地域を選択できます。
                    例：町の住民、国の指導者、地域の訪問者など。
                  </AlertDescription>
                </Alert>
                <Label htmlFor="community">関連コミュニティ（オプション）</Label>
                <Select
                  value={selectedCommunityId || 'none'}
                  onValueChange={(value) => {
                    if (value === 'none') {
                      setSelectedCommunityId('')
                      setCommunityRelationship('')
                      onInputChange({ 
                        ...input, 
                        communityId: undefined,
                        communityContext: undefined, 
                        communityRelationship: undefined 
                      })
                    } else {
                      setSelectedCommunityId(value)
                      const community = savedCommunities.find(c => c.id === value)
                      if (community) {
                        // 全データをJSON形式で渡す
                        const context = JSON.stringify(community.result)
                        onInputChange({ 
                          ...input, 
                          communityId: value,
                          communityContext: context
                        })
                      }
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="コミュニティを選択...">
                      {selectedCommunityId ? (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          {savedCommunities.find(c => c.id === selectedCommunityId)?.result.basicInfo.name}
                        </div>
                      ) : (
                        '選択しない'
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">選択しない</SelectItem>
                    {savedCommunities.map((community) => (
                      <SelectItem key={community.id} value={community.id}>
                        <div className="flex flex-col">
                          <div className="font-medium">
                            {community.result.basicInfo.name} ({community.result.basicInfo.type})
                          </div>
                          <div className="text-xs text-muted-foreground">
                            人口: {community.result.basicInfo.population?.toLocaleString() || '不明'}人
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {selectedCommunityId && (
                  <>
                    <div className="mt-3">
                      <Label htmlFor="communityRelationship">関係性の種類</Label>
                      <Select
                        value={communityRelationship || ''}
                        onValueChange={(value) => {
                          setCommunityRelationship(value)
                          onInputChange({ ...input, communityRelationship: value })
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="関係性を選択してください" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="resident">居住者（この地に住んでいる）</SelectItem>
                          <SelectItem value="visitor">訪問者（一時的に滞在）</SelectItem>
                          <SelectItem value="exile">追放者（かつて住んでいた）</SelectItem>
                          <SelectItem value="ruler">支配者・指導者</SelectItem>
                          <SelectItem value="guardian">守護者・防衛者</SelectItem>
                          <SelectItem value="merchant">商人・交易者</SelectItem>
                          <SelectItem value="criminal">犯罪者・無法者</SelectItem>
                          <SelectItem value="spy">密偵・諜報員</SelectItem>
                          <SelectItem value="refugee">難民・避難民</SelectItem>
                          <SelectItem value="other">その他</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {communityRelationship === 'other' && (
                      <div className="mt-2">
                        <Label htmlFor="communityRelationshipCustom">関係性の詳細</Label>
                        <Input
                          id="communityRelationshipCustom"
                          placeholder="具体的な関係性を記述してください"
                        />
                      </div>
                    )}
                    
                    <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                      <div className="font-medium text-blue-900 mb-1">選択中のコミュニティ</div>
                      <div className="text-blue-700">
                        {savedCommunities.find(c => c.id === selectedCommunityId)?.result.basicInfo.name}（
                        {savedCommunities.find(c => c.id === selectedCommunityId)?.result.basicInfo.type}）
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            
            {/* ソサエティ選択 */}
            {savedSocieties.length > 0 && (
              <div>
                <Alert className="mb-3">
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    キャラクターが所属または関連する組織・団体を選択できます。
                    例：ギルドメンバー、教団信者、盗賊団の首領など。
                  </AlertDescription>
                </Alert>
                <Label htmlFor="society">関連組織（オプション）</Label>
                <Select
                  value={selectedSocietyId || 'none'}
                  onValueChange={(value) => {
                    if (value === 'none') {
                      setSelectedSocietyId('')
                      setSocietyRelationship('')
                      onInputChange({ 
                        ...input, 
                        societyId: undefined,
                        societyContext: undefined, 
                        societyRelationship: undefined 
                      })
                    } else {
                      setSelectedSocietyId(value)
                      const society = savedSocieties.find(s => s.id === value)
                      if (society) {
                        // 全データをJSON形式で渡す
                        const context = JSON.stringify(society.result)
                        onInputChange({ 
                          ...input, 
                          societyId: value,
                          societyContext: context
                        })
                      }
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="組織を選択...">
                      {selectedSocietyId ? (
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4" />
                          {savedSocieties.find(s => s.id === selectedSocietyId)?.result.basicInfo.name}
                        </div>
                      ) : (
                        '選択しない'
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">選択しない</SelectItem>
                    {savedSocieties.map((society) => (
                      <SelectItem key={society.id} value={society.id}>
                        <div className="flex flex-col">
                          <div className="font-medium">
                            {society.result.basicInfo.name} ({society.result.basicInfo.type})
                          </div>
                          <div className="text-xs text-muted-foreground">
                            規模: {society.result.basicInfo.scale}
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {selectedSocietyId && (
                  <>
                    <div className="mt-3">
                      <Label htmlFor="societyRelationship">関係性の種類</Label>
                      <Select
                        value={societyRelationship || ''}
                        onValueChange={(value) => {
                          setSocietyRelationship(value)
                          onInputChange({ ...input, societyRelationship: value })
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="関係性を選択してください" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="member">一般構成員</SelectItem>
                          <SelectItem value="leader">指導者・幹部</SelectItem>
                          <SelectItem value="founder">創設者</SelectItem>
                          <SelectItem value="supporter">支援者・協力者</SelectItem>
                          <SelectItem value="enemy">敵対者</SelectItem>
                          <SelectItem value="former">元構成員</SelectItem>
                          <SelectItem value="spy">潜入者・スパイ</SelectItem>
                          <SelectItem value="client">依頼人・顧客</SelectItem>
                          <SelectItem value="victim">被害者</SelectItem>
                          <SelectItem value="other">その他</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {societyRelationship === 'other' && (
                      <div className="mt-2">
                        <Label htmlFor="societyRelationshipCustom">関係性の詳細</Label>
                        <Input
                          id="societyRelationshipCustom"
                          placeholder="具体的な関係性を記述してください"
                        />
                      </div>
                    )}
                    
                    <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                      <div className="font-medium text-blue-900 mb-1">選択中の組織</div>
                      <div className="text-blue-700">
                        {savedSocieties.find(s => s.id === selectedSocietyId)?.result.basicInfo.name}（
                        {savedSocieties.find(s => s.id === selectedSocietyId)?.result.basicInfo.type}）
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* アイテム選択 */}
            {savedItems.length > 0 && (
              <div className="space-y-2">
                <Label>関連アイテム（オプション）</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowItemSelector(!showItemSelector)}
                  className="w-full justify-between"
                >
                  <span className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    {selectedItemIds.length > 0
                      ? `${selectedItemIds.length}個のアイテムを選択中`
                      : 'アイテムを選択'}
                  </span>
                  {showItemSelector ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>

                {showItemSelector && (
                  <div className="border rounded-lg p-3 space-y-3 max-h-[300px] overflow-y-auto">
                    <Input
                      placeholder="アイテムを検索..."
                      value={itemSearchQuery}
                      onChange={(e) => setItemSearchQuery(e.target.value)}
                      className="h-8"
                    />
                    <div className="space-y-2">
                      {savedItems
                        .filter(item =>
                          item.result.basicInfo.name.toLowerCase().includes(itemSearchQuery.toLowerCase()) ||
                          (item.result.basicInfo.category || '').toLowerCase().includes(itemSearchQuery.toLowerCase())
                        )
                        .map(item => (
                          <div key={item.id} className="flex items-start gap-2 p-2 border rounded hover:bg-muted/50">
                            <Checkbox
                              checked={selectedItemIds.includes(item.id)}
                              onCheckedChange={(checked) => {
                                const newIds = checked
                                  ? [...selectedItemIds, item.id]
                                  : selectedItemIds.filter(id => id !== item.id)
                                setSelectedItemIds(newIds)
                                const selectedItems = newIds.map(id => savedItems.find(i => i.id === id)).filter(Boolean)
                                onInputChange({
                                  ...input,
                                  itemIds: newIds,
                                  itemsContext: JSON.stringify(selectedItems.map(i => i?.result)),
                                  itemRelationships
                                })
                              }}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm truncate">{item.result.basicInfo.name}</div>
                              <div className="text-xs text-muted-foreground">{item.result.basicInfo.category || item.result.basicInfo.type || 'アイテム'}</div>
                              {selectedItemIds.includes(item.id) && (
                                <Input
                                  placeholder="関係性（所持、作成、探索中等）"
                                  value={itemRelationships[item.id] || ''}
                                  onChange={(e) => {
                                    const newRels = { ...itemRelationships, [item.id]: e.target.value }
                                    setItemRelationships(newRels)
                                    onInputChange({ ...input, itemRelationships: newRels })
                                  }}
                                  className="mt-1 h-7 text-xs"
                                />
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* スキル選択 */}
            {savedSkills.length > 0 && (
              <div className="space-y-2">
                <Label>関連スキル（オプション）</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSkillSelector(!showSkillSelector)}
                  className="w-full justify-between"
                >
                  <span className="flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    {selectedSkillIds.length > 0
                      ? `${selectedSkillIds.length}個のスキルを選択中`
                      : 'スキルを選択'}
                  </span>
                  {showSkillSelector ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>

                {showSkillSelector && (
                  <div className="border rounded-lg p-3 space-y-3 max-h-[300px] overflow-y-auto">
                    <Input
                      placeholder="スキルを検索..."
                      value={skillSearchQuery}
                      onChange={(e) => setSkillSearchQuery(e.target.value)}
                      className="h-8"
                    />
                    <div className="space-y-2">
                      {savedSkills
                        .filter(skill =>
                          skill.result.basicInfo.name.toLowerCase().includes(skillSearchQuery.toLowerCase()) ||
                          (skill.result.basicInfo.type || '').toLowerCase().includes(skillSearchQuery.toLowerCase())
                        )
                        .map(skill => (
                          <div key={skill.id} className="flex items-start gap-2 p-2 border rounded hover:bg-muted/50">
                            <Checkbox
                              checked={selectedSkillIds.includes(skill.id)}
                              onCheckedChange={(checked) => {
                                const newIds = checked
                                  ? [...selectedSkillIds, skill.id]
                                  : selectedSkillIds.filter(id => id !== skill.id)
                                setSelectedSkillIds(newIds)
                                const selectedSkills = newIds.map(id => savedSkills.find(s => s.id === id)).filter(Boolean)
                                onInputChange({
                                  ...input,
                                  skillIds: newIds,
                                  skillsContext: JSON.stringify(selectedSkills.map(s => s?.result)),
                                  skillRelationships
                                })
                              }}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm truncate">{skill.result.basicInfo.name}</div>
                              <div className="text-xs text-muted-foreground">{skill.result.basicInfo.type || skill.result.basicInfo.tier || 'スキル'}</div>
                              {selectedSkillIds.includes(skill.id) && (
                                <Input
                                  placeholder="習得状況（習得済み、習得中、封印等）"
                                  value={skillRelationships[skill.id] || ''}
                                  onChange={(e) => {
                                    const newRels = { ...skillRelationships, [skill.id]: e.target.value }
                                    setSkillRelationships(newRels)
                                    onInputChange({ ...input, skillRelationships: newRels })
                                  }}
                                  className="mt-1 h-7 text-xs"
                                />
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* キーワード */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">キーワード（全カテゴリから3つ）</h3>
              <Button
                size="sm"
                variant="outline"
                onClick={rerollAllKeywords}
              >
                <RefreshCw className="h-4 w-4 mr-1" />
                全て再抽選
              </Button>
            </div>
            
            <div className="space-y-2">
              {keywords.map((keyword, index) => (
                <div key={index} className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggleKeywordMode(index)}
                  >
                    {keywordModes[index] === 'random' ? <Dices className="h-4 w-4" /> : '✏️'}
                  </Button>
                  {keywordModes[index] === 'random' ? (
                    <>
                      <Input
                        value={keyword.value}
                        readOnly
                        className="flex-1"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => rerollKeyword(index)}
                      >
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <Input
                      value={keyword.value}
                      onChange={(e) => updateCustomKeyword(index, e.target.value)}
                      placeholder="カスタムキーワードを入力"
                      className="flex-1"
                    />
                  )}
                </div>
              ))}
            </div>
            
            <p className="text-xs text-muted-foreground">
              850個以上のキーワードから自動選択されます（性格、背景、動機、職業、恐れ、スキル、外見、癖、人間関係、感情）
            </p>
          </div>

          {/* 追加設定 */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="additionalSettings">追加設定（任意）</Label>
              <span className="text-xs text-muted-foreground">
                {additionalSettings ? `${additionalSettings.length}文字` : ''}
              </span>
            </div>
            <Textarea
              id="additionalSettings"
              value={additionalSettings}
              onChange={(e) => setAdditionalSettings(e.target.value)}
              placeholder="キャラクターに関する追加の要望や設定があれば記入してください（例：必ず関西弁で話す、左利き、特定の趣味を持つなど）"
              rows={3}
              className="text-sm"
            />
            <p className="text-xs text-muted-foreground">
              ここに記載した内容は生成時に考慮されます（記入しなくても生成可能です）
            </p>
          </div>
        </TabsContent>

        <TabsContent value="idea" className="space-y-4">
          {/* 名前指定 (オプション) */}
          <div className="space-y-2">
            <Label>キャラクター名（オプション）</Label>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={nameMode === 'auto' ? 'default' : 'outline'}
                onClick={() => setNameMode('auto')}
              >
                お任せ
              </Button>
              <Button
                size="sm"
                variant={nameMode === 'custom' ? 'default' : 'outline'}
                onClick={() => setNameMode('custom')}
              >
                指定する
              </Button>
            </div>
            {nameMode === 'custom' && (
              <Input
                value={input.name || ''}
                onChange={(e) => onInputChange({ ...input, name: e.target.value })}
                placeholder="例：山田太郎（空欄の場合はお任せ）"
              />
            )}
            <p className="text-xs text-muted-foreground">
              名前を指定すると、アイディアや参考ファイル内の名前指定より優先されます
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label htmlFor="ideaText">キャラクターのアイディア</Label>
              <div className="text-sm text-muted-foreground">
                {input.ideaText ? `${input.ideaText.length}文字` : ''}
              </div>
            </div>
            <Textarea
              id="ideaText"
              value={input.ideaText || ''}
              onChange={(e) => onInputChange({ ...input, ideaText: e.target.value })}
              placeholder="キャラクターのアイディアを自由に入力してください"
              rows={6}
              className="font-mono text-sm"
            />
          </div>

          {/* 世界観選択 */}
          {savedWorlds.length > 0 && (
            <div>
              <Label htmlFor="world">世界観を選択（オプション）</Label>
              <Select 
                value={selectedWorldId || 'none'} 
                onValueChange={(value) => {
                  if (value === 'none') {
                    setSelectedWorldId('')
                    // 世界観を削除
                    const currentIdea = input.ideaText || ''
                    const cleanedIdea = currentIdea.replace(/【世界観】[\s\S]*?(?=\n\n|$)/, '').trim()
                    onInputChange({ ...input, ideaText: cleanedIdea })
                  } else {
                    setSelectedWorldId(value)
                    const world = savedWorlds.find(w => w.id === value)
                    if (world) {
                      const worldContext = generateWorldContextText(world.result)
                      // アイディアモードでは ideaText に追加
                      const currentIdea = input.ideaText || ''
                      // 【世界名】から始まる世界観セクションを削除して新しいものを追加
                      const cleanedIdea = currentIdea.replace(/【世界名】[\s\S]*?(?=【[^世]|$)/g, '').trim()
                      onInputChange({ 
                        ...input, 
                        ideaText: cleanedIdea ? `${cleanedIdea}\n\n${worldContext}` : worldContext 
                      })
                    }
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="世界観を選択...">
                    {selectedWorldId ? (
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4" />
                        {savedWorlds.find(w => w.id === selectedWorldId)?.title}
                      </div>
                    ) : (
                      '世界観を選択...'
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">選択しない</SelectItem>
                  {savedWorlds.map((world) => (
                    <SelectItem key={world.id} value={world.id}>
                      <div className="flex flex-col">
                        <div className="font-medium">{world.title}</div>
                        <div className="text-xs text-muted-foreground">
                          {world.result.summary?.substring(0, 50)}...
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedWorldId && (
                <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                  <div className="font-medium text-blue-900 mb-1">選択中の世界観</div>
                  <div className="text-blue-700">
                    {savedWorlds.find(w => w.id === selectedWorldId)?.result.summary?.substring(0, 100)}...
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* 関連キャラクター選択（アイディアモード） */}
          {characters.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>関連キャラクター（オプション）</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowCharacterSelector(!showCharacterSelector)}
                >
                  {showCharacterSelector ? (
                    <>
                      <ChevronUp className="h-4 w-4 mr-1" />
                      閉じる
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4 mr-1" />
                      開く ({selectedCharacterIds.length}/{characters.length}人選択中)
                    </>
                  )}
                </Button>
              </div>
              
              {showCharacterSelector && (
                <>
                  {/* 検索バー */}
                  {characters.length > 5 && (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="名前、役割、年齢などで検索..."
                        value={characterSearchQuery}
                        onChange={(e) => setCharacterSearchQuery(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  )}
                  
                  {/* キャラクターリスト */}
                  <div className="p-3 border rounded-lg space-y-2 max-h-48 overflow-y-auto">
                    {(() => {
                      const filteredCharacters = characters.filter(character => {
                        if (!characterSearchQuery) return true
                        const query = characterSearchQuery.toLowerCase()
                        return (
                          character.result.name.toLowerCase().includes(query) ||
                          character.result.role.toLowerCase().includes(query) ||
                          character.result.age?.toLowerCase().includes(query) ||
                          character.result.summary?.toLowerCase().includes(query)
                        )
                      })
                      
                      if (filteredCharacters.length === 0) {
                        return (
                          <p className="text-sm text-muted-foreground text-center py-2">
                            該当するキャラクターが見つかりません
                          </p>
                        )
                      }
                      
                      return filteredCharacters.map(character => (
                        <div key={character.id} className="flex items-start gap-2">
                          <Checkbox
                            id={`char-idea-${character.id}`}
                            checked={selectedCharacterIds.includes(character.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedCharacterIds([...selectedCharacterIds, character.id])
                              } else {
                                setSelectedCharacterIds(selectedCharacterIds.filter(id => id !== character.id))
                                // 関係性も削除
                                const newRelationships = { ...characterRelationships }
                                delete newRelationships[character.id]
                                setCharacterRelationships(newRelationships)
                              }
                              
                              // inputを更新
                              const newIds = checked 
                                ? [...selectedCharacterIds, character.id]
                                : selectedCharacterIds.filter(id => id !== character.id)
                              
                              if (newIds.length > 0) {
                                const selectedChars = newIds.map(id => characters.find(c => c.id === id)).filter(Boolean)
                                const contextsJson = JSON.stringify(selectedChars.map(c => c.result))
                                onInputChange({ 
                                  ...input, 
                                  relatedCharacterIds: newIds,
                                  relatedCharactersContext: contextsJson,
                                  characterRelationships: characterRelationships
                                })
                              } else {
                                onInputChange({ 
                                  ...input, 
                                  relatedCharacterIds: undefined,
                                  relatedCharactersContext: undefined,
                                  characterRelationships: undefined
                                })
                              }
                            }}
                          />
                          <label 
                            htmlFor={`char-idea-${character.id}`} 
                            className="flex-1 cursor-pointer text-sm"
                          >
                            <div className="font-medium">{character.result.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {character.result.role} / {character.result.age} / {character.result.gender}
                            </div>
                          </label>
                        </div>
                      ))
                    })()}
                  </div>
                  
                  {/* 選択されたキャラクターとの関係性入力 */}
                  {selectedCharacterIds.length > 0 && (
                    <div className="space-y-2 mt-3">
                      <Label>各キャラクターとの関係性</Label>
                      {selectedCharacterIds.map(charId => {
                        const character = characters.find(c => c.id === charId)
                        if (!character) return null
                        
                        return (
                          <div key={charId} className="flex items-center gap-2">
                            <span className="text-sm w-24 truncate" title={character.result.name}>
                              {character.result.name}:
                            </span>
                            <Input
                              value={characterRelationships[charId] || ''}
                              onChange={(e) => {
                                const newRelationships = {
                                  ...characterRelationships,
                                  [charId]: e.target.value
                                }
                                setCharacterRelationships(newRelationships)
                                onInputChange({ 
                                  ...input, 
                                  characterRelationships: newRelationships
                                })
                              }}
                              placeholder="例：兄弟、恋人、ライバル、師弟など"
                              className="flex-1"
                            />
                          </div>
                        )
                      })}
                    </div>
                  )}
                </>
              )}
              
              {/* 選択されたキャラクターのサマリー */}
              {selectedCharacterIds.length > 0 && (
                <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                  <div className="font-medium text-blue-900 mb-1">
                    選択中のキャラクター ({selectedCharacterIds.length}人)
                  </div>
                  <div className="text-blue-700 space-y-1">
                    {selectedCharacterIds.slice(0, 3).map(id => {
                      const character = characters.find(c => c.id === id)
                      if (!character) return null
                      return (
                        <div key={id}>
                          {character.result.name}: {character.result.summary?.substring(0, 50)}...
                        </div>
                      )
                    })}
                    {selectedCharacterIds.length > 3 && (
                      <div>他 {selectedCharacterIds.length - 3} 人...</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* コミュニティ選択（アイディアモード） */}
          {savedCommunities.length > 0 && (
            <div>
              <Alert className="mb-3">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  キャラクターが所属または関連する都市・国・地域を選択できます。
                </AlertDescription>
              </Alert>
              <Label htmlFor="communityIdea">関連コミュニティ（オプション）</Label>
              <Select
                value={selectedCommunityId || 'none'}
                onValueChange={(value) => {
                  if (value === 'none') {
                    setSelectedCommunityId('')
                    setCommunityRelationship('')
                    // アイディアテキストからコミュニティ情報を削除
                    const currentIdea = input.ideaText || ''
                    const cleanedIdea = currentIdea.replace(/【コミュニティ：[^】]*】[\s\S]*?(?=【|$)/g, '').trim()
                    onInputChange({ 
                      ...input, 
                      ideaText: cleanedIdea,
                      communityId: undefined,
                      communityContext: undefined, 
                      communityRelationship: undefined 
                    })
                  } else {
                    setSelectedCommunityId(value)
                    const community = savedCommunities.find(c => c.id === value)
                    if (community) {
                      // アイディアテキストにコミュニティ情報を追加
                      const currentIdea = input.ideaText || ''
                      const cleanedIdea = currentIdea.replace(/【コミュニティ：[^】]*】[\s\S]*?(?=【|$)/g, '').trim()
                      const communityText = `【コミュニティ：${community.result.basicInfo.name}】\n${JSON.stringify(community.result)}`
                      onInputChange({ 
                        ...input, 
                        ideaText: cleanedIdea ? `${cleanedIdea}\n\n${communityText}` : communityText,
                        communityId: value,
                        communityContext: JSON.stringify(community.result)
                      })
                    }
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="コミュニティを選択...">
                    {selectedCommunityId ? (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        {savedCommunities.find(c => c.id === selectedCommunityId)?.result.basicInfo.name}
                      </div>
                    ) : (
                      '選択しない'
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">選択しない</SelectItem>
                  {savedCommunities.map((community) => (
                    <SelectItem key={community.id} value={community.id}>
                      <div className="flex flex-col">
                        <div className="font-medium">
                          {community.result.basicInfo.name} ({community.result.basicInfo.type})
                        </div>
                        <div className="text-xs text-muted-foreground">
                          人口: {community.result.basicInfo.population?.toLocaleString() || '不明'}人
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {selectedCommunityId && (
                <>
                  <div className="mt-3">
                    <Label htmlFor="communityRelationshipIdea">関係性の種類</Label>
                    <Select
                      value={communityRelationship || ''}
                      onValueChange={(value) => {
                        setCommunityRelationship(value)
                        onInputChange({ ...input, communityRelationship: value })
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="関係性を選択してください" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="resident">居住者（この地に住んでいる）</SelectItem>
                        <SelectItem value="visitor">訪問者（一時的に滞在）</SelectItem>
                        <SelectItem value="exile">追放者（かつて住んでいた）</SelectItem>
                        <SelectItem value="ruler">支配者・指導者</SelectItem>
                        <SelectItem value="guardian">守護者・防衛者</SelectItem>
                        <SelectItem value="merchant">商人・交易者</SelectItem>
                        <SelectItem value="criminal">犯罪者・無法者</SelectItem>
                        <SelectItem value="spy">密偵・諜報員</SelectItem>
                        <SelectItem value="refugee">難民・避難民</SelectItem>
                        <SelectItem value="other">その他</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          )}
          
          {/* ソサエティ選択（アイディアモード） */}
          {savedSocieties.length > 0 && (
            <div>
              <Alert className="mb-3">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  キャラクターが所属または関連する組織・団体を選択できます。
                </AlertDescription>
              </Alert>
              <Label htmlFor="societyIdea">関連組織（オプション）</Label>
              <Select
                value={selectedSocietyId || 'none'}
                onValueChange={(value) => {
                  if (value === 'none') {
                    setSelectedSocietyId('')
                    setSocietyRelationship('')
                    // アイディアテキストから組織情報を削除
                    const currentIdea = input.ideaText || ''
                    const cleanedIdea = currentIdea.replace(/【組織：[^】]*】[\s\S]*?(?=【|$)/g, '').trim()
                    onInputChange({ 
                      ...input, 
                      ideaText: cleanedIdea,
                      societyId: undefined,
                      societyContext: undefined, 
                      societyRelationship: undefined 
                    })
                  } else {
                    setSelectedSocietyId(value)
                    const society = savedSocieties.find(s => s.id === value)
                    if (society) {
                      // アイディアテキストに組織情報を追加
                      const currentIdea = input.ideaText || ''
                      const cleanedIdea = currentIdea.replace(/【組織：[^】]*】[\s\S]*?(?=【|$)/g, '').trim()
                      const societyText = `【組織：${society.result.basicInfo.name}】\n${JSON.stringify(society.result)}`
                      onInputChange({ 
                        ...input, 
                        ideaText: cleanedIdea ? `${cleanedIdea}\n\n${societyText}` : societyText,
                        societyId: value,
                        societyContext: JSON.stringify(society.result)
                      })
                    }
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="組織を選択...">
                    {selectedSocietyId ? (
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        {savedSocieties.find(s => s.id === selectedSocietyId)?.result.basicInfo.name}
                      </div>
                    ) : (
                      '選択しない'
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">選択しない</SelectItem>
                  {savedSocieties.map((society) => (
                    <SelectItem key={society.id} value={society.id}>
                      <div className="flex flex-col">
                        <div className="font-medium">
                          {society.result.basicInfo.name} ({society.result.basicInfo.type})
                        </div>
                        <div className="text-xs text-muted-foreground">
                          規模: {society.result.basicInfo.scale}
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {selectedSocietyId && (
                <>
                  <div className="mt-3">
                    <Label htmlFor="societyRelationshipIdea">関係性の種類</Label>
                    <Select
                      value={societyRelationship || ''}
                      onValueChange={(value) => {
                        setSocietyRelationship(value)
                        onInputChange({ ...input, societyRelationship: value })
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="関係性を選択してください" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="member">一般構成員</SelectItem>
                        <SelectItem value="leader">指導者・幹部</SelectItem>
                        <SelectItem value="founder">創設者</SelectItem>
                        <SelectItem value="supporter">支援者・協力者</SelectItem>
                        <SelectItem value="enemy">敵対者</SelectItem>
                        <SelectItem value="former">元構成員</SelectItem>
                        <SelectItem value="spy">潜入者・スパイ</SelectItem>
                        <SelectItem value="client">依頼人・顧客</SelectItem>
                        <SelectItem value="victim">被害者</SelectItem>
                        <SelectItem value="other">その他</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          )}

          {/* アイテム選択（アイディアモード） */}
          {savedItems.length > 0 && (
            <div>
              <Alert className="mb-3">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  キャラクターが所持・関連するアイテムを選択できます。
                </AlertDescription>
              </Alert>
              <Label>関連アイテム（オプション）</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowItemSelector(!showItemSelector)}
                className="w-full justify-between mt-2"
              >
                <span className="flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  {selectedItemIds.length > 0
                    ? `${selectedItemIds.length}個のアイテムを選択中`
                    : 'アイテムを選択'}
                </span>
                {showItemSelector ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>

              {showItemSelector && (
                <div className="border rounded-lg p-3 space-y-3 max-h-[300px] overflow-y-auto mt-2">
                  <Input
                    placeholder="アイテムを検索..."
                    value={itemSearchQuery}
                    onChange={(e) => setItemSearchQuery(e.target.value)}
                    className="h-8"
                  />
                  <div className="space-y-2">
                    {savedItems
                      .filter(item =>
                        item.result.basicInfo.name.toLowerCase().includes(itemSearchQuery.toLowerCase()) ||
                        (item.result.basicInfo.category || '').toLowerCase().includes(itemSearchQuery.toLowerCase())
                      )
                      .map(item => (
                        <div key={item.id} className="flex items-start gap-2 p-2 border rounded hover:bg-muted/50">
                          <Checkbox
                            checked={selectedItemIds.includes(item.id)}
                            onCheckedChange={(checked) => {
                              const newIds = checked
                                ? [...selectedItemIds, item.id]
                                : selectedItemIds.filter(id => id !== item.id)
                              setSelectedItemIds(newIds)
                              const selectedItems = newIds.map(id => savedItems.find(i => i.id === id)).filter(Boolean)
                              onInputChange({
                                ...input,
                                itemIds: newIds,
                                itemsContext: JSON.stringify(selectedItems.map(i => i?.result)),
                                itemRelationships
                              })
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate">{item.result.basicInfo.name}</div>
                            <div className="text-xs text-muted-foreground">{item.result.basicInfo.category || item.result.basicInfo.type || 'アイテム'}</div>
                            {selectedItemIds.includes(item.id) && (
                              <Input
                                placeholder="関係性（所持、作成、探索中等）"
                                value={itemRelationships[item.id] || ''}
                                onChange={(e) => {
                                  const newRels = { ...itemRelationships, [item.id]: e.target.value }
                                  setItemRelationships(newRels)
                                  onInputChange({ ...input, itemRelationships: newRels })
                                }}
                                className="mt-1 h-7 text-xs"
                              />
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* スキル選択（アイディアモード） */}
          {savedSkills.length > 0 && (
            <div>
              <Alert className="mb-3">
                <Info className="h-4 w-4" />
                <AlertDescription>
                  キャラクターが習得・関連するスキルを選択できます。
                </AlertDescription>
              </Alert>
              <Label>関連スキル（オプション）</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSkillSelector(!showSkillSelector)}
                className="w-full justify-between mt-2"
              >
                <span className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  {selectedSkillIds.length > 0
                    ? `${selectedSkillIds.length}個のスキルを選択中`
                    : 'スキルを選択'}
                </span>
                {showSkillSelector ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>

              {showSkillSelector && (
                <div className="border rounded-lg p-3 space-y-3 max-h-[300px] overflow-y-auto mt-2">
                  <Input
                    placeholder="スキルを検索..."
                    value={skillSearchQuery}
                    onChange={(e) => setSkillSearchQuery(e.target.value)}
                    className="h-8"
                  />
                  <div className="space-y-2">
                    {savedSkills
                      .filter(skill =>
                        skill.result.basicInfo.name.toLowerCase().includes(skillSearchQuery.toLowerCase()) ||
                        (skill.result.basicInfo.type || '').toLowerCase().includes(skillSearchQuery.toLowerCase())
                      )
                      .map(skill => (
                        <div key={skill.id} className="flex items-start gap-2 p-2 border rounded hover:bg-muted/50">
                          <Checkbox
                            checked={selectedSkillIds.includes(skill.id)}
                            onCheckedChange={(checked) => {
                              const newIds = checked
                                ? [...selectedSkillIds, skill.id]
                                : selectedSkillIds.filter(id => id !== skill.id)
                              setSelectedSkillIds(newIds)
                              const selectedSkills = newIds.map(id => savedSkills.find(s => s.id === id)).filter(Boolean)
                              onInputChange({
                                ...input,
                                skillIds: newIds,
                                skillsContext: JSON.stringify(selectedSkills.map(s => s?.result)),
                                skillRelationships
                              })
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate">{skill.result.basicInfo.name}</div>
                            <div className="text-xs text-muted-foreground">{skill.result.basicInfo.type || skill.result.basicInfo.tier || 'スキル'}</div>
                            {selectedSkillIds.includes(skill.id) && (
                              <Input
                                placeholder="習得状況（習得済み、習得中、封印等）"
                                value={skillRelationships[skill.id] || ''}
                                onChange={(e) => {
                                  const newRels = { ...skillRelationships, [skill.id]: e.target.value }
                                  setSkillRelationships(newRels)
                                  onInputChange({ ...input, skillRelationships: newRels })
                                }}
                                className="mt-1 h-7 text-xs"
                              />
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 保存済みプロットから選択 */}
          {savedPlots.length > 0 && (
            <div>
              <Label htmlFor="plotSelect">保存済みプロットから選択</Label>
              <Select value={selectedPlotId} onValueChange={handlePlotSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="プロットを選択..." />
                </SelectTrigger>
                <SelectContent>
                  {savedPlots.map(plot => (
                    <SelectItem key={plot.id} value={plot.id}>
                      {plot.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedPlotId && (
                <div className="mt-2 p-3 bg-muted rounded-md">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      選択されたプロット: {savedPlots.find(p => p.id === selectedPlotId)?.title}
                    </p>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setSelectedPlotId('')
                        onInputChange({ ...input, plotContext: '' })
                      }}
                    >
                      クリア
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* プロットコンテキストの表示・編集 */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label htmlFor="plotContext">
                {selectedPlotId ? 'プロットコンテキスト（選択されたプロット）' : 'プロットコンテキスト（手動入力）'}
              </Label>
              <div className="text-sm text-muted-foreground">
                {input.plotContext ? `${input.plotContext.length}文字` : ''}
              </div>
            </div>
            <Textarea
              id="plotContext"
              value={input.plotContext || ''}
              onChange={(e) => onInputChange({ ...input, plotContext: e.target.value })}
              placeholder={selectedPlotId ? '選択されたプロットの内容が表示されます' : '既存のプロットを貼り付けることでキャラクターを合わせられます'}
              rows={6}
              className="font-mono text-sm"
            />
            {selectedPlotId && (
              <p className="text-xs text-muted-foreground mt-1">
                ※ 上記の内容は編集可能です。必要に応じて調整してください
              </p>
            )}
          </div>

          {/* ファイルアップロード */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label htmlFor="fileUpload">参考ファイルアップロード</Label>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-4 w-4 mr-2" />
                ファイルを選択
              </Button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={FILE_ACCEPT}
              multiple
              onChange={handleFileUpload}
              className="hidden"
            />
            
            {/* 読み込んだファイルのリスト */}
            {uploadedFiles.length > 0 && (
              <div className="space-y-2">
                <div className="p-3 bg-gray-50 rounded-lg border">
                  <div className="text-sm font-medium text-gray-700 mb-2">読み込み済みファイル：</div>
                  <div className="space-y-2">
                    {uploadedFiles.map((file) => (
                      <div key={file.id} className="flex items-center justify-between bg-white p-2 rounded border">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-gray-500" />
                          <span className="text-sm">{file.name}</span>
                          <span className="text-xs text-gray-500">({formatFileSize(file.size)})</span>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => handleFileRemove(file.id)}
                          className="p-1"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <FileText className="h-4 w-4" />
                  <span>
                    合計 {uploadedFiles.length} ファイル（{formatFileSize(uploadedFiles.reduce((sum, f) => sum + f.size, 0))}）
                  </span>
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              複数のファイルを同時にアップロード可能です（対応形式: .txt, .md, .docx, .pdf）
            </p>
          </div>

          {/* 画像アップロード */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label htmlFor="imageUpload">参考画像アップロード</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => imageInputRef.current?.click()}
                disabled={uploadedImages.length >= 5}
              >
                <Image className="h-4 w-4 mr-2" />
                画像を選択 ({uploadedImages.length}/5)
              </Button>
            </div>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              multiple
              onChange={handleImageUpload}
              className="hidden"
            />

            {/* 読み込んだ画像のプレビュー */}
            {uploadedImages.length > 0 && (
              <div className="space-y-2">
                <div className="p-3 bg-gray-50 rounded-lg border">
                  <div className="text-sm font-medium text-gray-700 mb-2">読み込み済み画像：</div>
                  <div className="flex flex-wrap gap-2">
                    {uploadedImages.map((image) => (
                      <div key={image.id} className="relative group">
                        <img
                          src={image.data}
                          alt={image.name}
                          className="h-20 w-20 object-cover rounded border"
                        />
                        <button
                          type="button"
                          onClick={() => handleImageRemove(image.id)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs px-1 py-0.5 truncate">
                          {image.name}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Image className="h-4 w-4" />
                  <span>
                    合計 {uploadedImages.length} 画像（{formatFileSize(uploadedImages.reduce((sum, img) => sum + img.size, 0))}）
                  </span>
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              キャラクターの外見参考として画像をアップロードできます（対応形式: JPEG/PNG/GIF/WebP、最大5枚、各20MBまで）
            </p>
          </div>
        </TabsContent>
      </Tabs>
      )}

      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>創造性レベル</Label>
            <span className="text-sm text-muted-foreground">{input.creativityLevel || 7}</span>
          </div>
          <Slider
            value={[input.creativityLevel || 7]}
            onValueChange={([value]) => onInputChange({ ...input, creativityLevel: value })}
            min={1}
            max={10}
            step={1}
          />
        </div>

        <div>
          <Label htmlFor="seed">シード値</Label>
          <Input
            id="seed"
            type="number"
            value={input.seed || ''}
            onChange={(e) => onInputChange({ ...input, seed: e.target.value ? parseInt(e.target.value) : undefined })}
            placeholder="空欄でランダム"
          />
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="proMode"
            checked={input.useProMode || false}
            onCheckedChange={(checked) => onInputChange({ ...input, useProMode: checked as boolean })}
          />
          <Label htmlFor="proMode" className="cursor-pointer">Proモード（より高品質な生成）</Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="gemini3FlashMode"
            checked={input.useGemini3FlashMode || false}
            onCheckedChange={(checked) => onInputChange({ ...input, useGemini3FlashMode: checked as boolean })}
          />
          <Label htmlFor="gemini3FlashMode" className="cursor-pointer">Gemini 3 Flash モード（試験版）</Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="gemini3Mode"
            checked={input.useGemini3Mode || false}
            onCheckedChange={(checked) => onInputChange({ ...input, useGemini3Mode: checked as boolean })}
          />
          <Label htmlFor="gemini3Mode" className="cursor-pointer">Gemini 3 Pro モード（有料APIのみ・試験版）</Label>
        </div>

        {onStreamingChange && (
          <div className="flex items-center space-x-2">
            <Checkbox
              id="streaming"
              checked={useStreaming || false}
              onCheckedChange={(checked) => onStreamingChange(checked as boolean)}
            />
            <Label htmlFor="streaming" className="cursor-pointer">ストリーミングを使用（リアルタイム生成・進捗表示）</Label>
          </div>
        )}
      </div>

      <Button 
        onClick={() => {
          const preparedInput = prepareInput()
          onGenerate(preparedInput)
        }} 
        disabled={isGenerating} 
        className="w-full"
      >
        {isGenerating ? '生成中...' : partialRegenerateMode ? '選択部分を再生成' : 'キャラクター設定を生成'}
      </Button>
    </div>
  )
}